'use client'

import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import Image from 'next/image'
import AppIcon from '@/components/content/app/AppIcon'
import { Icon } from '@/icons/Icon'
import { lightbox } from '@/lib/lightbox'
import { extractTextFromLexical } from '@/lib/lexical-json/extract'
import { cn, getBrandColors } from '@/lib/utils'
import type { App, Media } from '@/payload-types'

type ScreenshotApp = Pick<
  App,
  'name' | 'icon' | 'glyph' | 'brandPrimaryColor' | 'brandBackgroundColor'
>

type ScreenshotEntry = {
  image: Media
  app: ScreenshotApp
}

type ScreenshotCarouselProps = {
  entries: ScreenshotEntry[]
  alwaysShowThumbnails?: boolean
}

function buildEntries(
  apps: Pick<
    App,
    'name' | 'icon' | 'glyph' | 'brandPrimaryColor' | 'brandBackgroundColor' | 'screenshots'
  >[],
): ScreenshotEntry[] {
  return apps.flatMap((app) => {
    const docs = (app.screenshots?.docs || []) as Media[]
    return docs
      .filter((img) => img?.url)
      .sort((a, b) => (a.screenshotOrder ?? Infinity) - (b.screenshotOrder ?? Infinity))
      .map((img) => ({
        image: img,
        app: {
          name: app.name,
          icon: app.icon,
          glyph: app.glyph,
          brandPrimaryColor: app.brandPrimaryColor,
          brandBackgroundColor: app.brandBackgroundColor,
        },
      }))
  })
}

export { buildEntries as buildScreenshotEntries }

export default function ScreenshotCarousel({
  entries,
  alwaysShowThumbnails = false,
}: ScreenshotCarouselProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const thumbnailsRef = useRef<HTMLDivElement>(null)
  const isMultiApp = useMemo(() => new Set(entries.map((e) => e.app.name)).size > 1, [entries])

  // Reset selection when entries change (e.g. tab switch on compare page)
  useEffect(() => {
    setSelectedIndex(0)
  }, [entries])

  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(() => lightbox.init())
      return () => {
        window.cancelIdleCallback(id)
        lightbox.destroy()
      }
    } else {
      const timer = setTimeout(() => lightbox.init(), 200)
      return () => {
        clearTimeout(timer)
        lightbox.destroy()
      }
    }
  }, [])

  const scrollThumbnailIntoView = useCallback((index: number) => {
    const container = thumbnailsRef.current
    if (!container) return
    const thumb = container.children[index] as HTMLElement | undefined
    if (!thumb) return
    thumb.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' })
  }, [])

  const goTo = useCallback(
    (index: number) => {
      setSelectedIndex(index)
      scrollThumbnailIntoView(index)
    },
    [scrollThumbnailIntoView],
  )

  const goPrev = useCallback(() => {
    const next = selectedIndex > 0 ? selectedIndex - 1 : entries.length - 1
    goTo(next)
  }, [selectedIndex, entries.length, goTo])

  const goNext = useCallback(() => {
    const next = selectedIndex < entries.length - 1 ? selectedIndex + 1 : 0
    goTo(next)
  }, [selectedIndex, entries.length, goTo])

  if (!entries.length) return null

  const selected = entries[selectedIndex]
  if (!selected) return null

  const { image, app } = selected
  const appIcon = app.icon as Media | undefined
  const appGlyph = app.glyph as Media | undefined
  const { backgroundColor } = getBrandColors(app)
  const caption = image.caption ? extractTextFromLexical(image.caption) : null
  const brandColor = app.brandPrimaryColor || '#6366f1'

  return (
    <div className="flex flex-col gap-3" id="rich-content">
      {/* Main Image — fixed aspect ratio with crossfade */}
      <div className="relative aspect-[16/10] overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
        {entries.map((entry, i) => {
          const isActive = i === selectedIndex
          const isAdjacent =
            i === selectedIndex - 1 ||
            i === selectedIndex + 1 ||
            (selectedIndex === 0 && i === entries.length - 1) ||
            (selectedIndex === entries.length - 1 && i === 0)
          if (!isActive && !isAdjacent) return null
          return (
            <a
              key={entry.image.id ?? i}
              href={entry.image.url!}
              data-pswp-width={entry.image.width}
              data-pswp-height={entry.image.height}
              className={cn(
                'lightbox-image absolute inset-0 transition-opacity duration-300',
                isActive ? 'z-10 opacity-100' : 'pointer-events-none z-0 opacity-0',
              )}
              tabIndex={isActive ? 0 : -1}
            >
              <Image
                src={entry.image.url!}
                alt={entry.image.alt || `${entry.app.name} screenshot`}
                fill
                sizes="(max-width: 768px) 100vw, 800px"
                className="object-contain"
                priority={i === 0}
              />
            </a>
          )
        })}

        {/* Bottom gradient overlay with app info */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-4 pb-4 pt-16">
          <div className="flex items-center gap-3">
            <AppIcon
              icon={appIcon}
              glyph={appGlyph}
              name={app.name}
              background={backgroundColor}
              size={36}
              transition={false}
            />
            <div className="min-w-0">
              <p className="text-14 font-semibold leading-tight text-white">{app.name}</p>
              {caption && (
                <p className="mt-0.5 line-clamp-1 text-13 leading-tight text-white/70">{caption}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Thumbnail strip */}
      {(entries.length > 1 || alwaysShowThumbnails) && (
        <div className="flex items-center gap-2">
          <button
            onClick={goPrev}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
            aria-label="Previous screenshot"
          >
            <Icon name="chevron-left" size={12} />
          </button>

          <div
            ref={thumbnailsRef}
            className="hide-scrollbar flex flex-1 gap-2 overflow-x-auto scroll-smooth"
          >
            {entries.map((entry, i) => {
              const thumbAppIcon = entry.app.icon as Media | undefined
              const thumbAppGlyph = entry.app.glyph as Media | undefined
              const { backgroundColor: thumbBg } = getBrandColors(entry.app)
              return (
                <button
                  key={entry.image.id ?? i}
                  onClick={() => goTo(i)}
                  className={cn(
                    'relative h-16 w-24 shrink-0 overflow-hidden rounded-lg border-2 transition-all',
                    i === selectedIndex ? '' : 'border-transparent opacity-70 hover:opacity-100',
                  )}
                  style={i === selectedIndex ? { borderColor: brandColor } : undefined}
                  aria-label={`View screenshot ${i + 1}`}
                >
                  <Image
                    src={entry.image.url!}
                    alt={entry.image.alt || `Screenshot ${i + 1}`}
                    fill
                    sizes="96px"
                    className="object-cover"
                  />
                  {isMultiApp && (
                    <div className="absolute bottom-1 left-1 z-10">
                      <AppIcon
                        icon={thumbAppIcon}
                        glyph={thumbAppGlyph}
                        name={entry.app.name}
                        background={thumbBg}
                        size={16}
                        transition={false}
                      />
                    </div>
                  )}
                </button>
              )
            })}
          </div>

          <button
            onClick={goNext}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
            aria-label="Next screenshot"
          >
            <Icon name="chevron-left" size={12} className="rotate-180" />
          </button>
        </div>
      )}
    </div>
  )
}
